import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { ChevronDown, ChevronUp, Search } from '@saleshandy/icons';
import './simple-options-dropdown.scss';

export type SimpleDropdownOption = {
  key: string | number;
  label: string;
  /** Used with `getCallOutcomeIcon` / custom icons (e.g. call outcomes) */
  isDefault?: boolean;
  sentiment?: string;
};

export type SimpleOptionsDropdownProps = {
  options: SimpleDropdownOption[];
  /** Selected option key; omit or null when nothing is selected */
  selectedKey?: string | number | null;
  /**
   * Shown in the trigger when `selectedKey` is set but no matching option exists in `options`
   * (e.g. list still loading).
   */
  fallbackLabel?: string;
  /**
   * Full option shape for icon/title when the selected row is not yet in `options`
   * but `selectedKey` + `fallbackLabel` apply (e.g. current outcome before list loads).
   */
  fallbackOption?: SimpleDropdownOption;
  onChange: (option: SimpleDropdownOption) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Applied to the floating list panel */
  menuClassName?: string;
  /** Search box above the list; filters by `label` (case-insensitive) */
  showSearch?: boolean;
  searchPlaceholder?: string;
  /** Renders a leading icon per option (and in the trigger when selected) */
  renderOptionIcon?: (option: SimpleDropdownOption) => React.ReactNode;
};

const SimpleOptionsDropdown: React.FC<SimpleOptionsDropdownProps> = memo(
  ({
    options,
    selectedKey,
    fallbackLabel,
    fallbackOption,
    onChange,
    placeholder = 'Select…',
    disabled = false,
    className,
    menuClassName,
    showSearch = false,
    searchPlaceholder = 'Search',
    renderOptionIcon,
  }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
      if (options.length === 0) {
        setOpen(false);
      }
    }, [options.length]);

    useEffect(() => {
      if (!open) {
        setSearchQuery('');
      }
    }, [open]);

    useEffect(() => {
      if (open && showSearch) {
        searchInputRef.current?.focus();
      }
    }, [open, showSearch]);

    const selected = useMemo(() => options.find((o) => o.key === selectedKey), [
      options,
      selectedKey,
    ]);

    const selectedForIcon = useMemo(() => {
      if (selected) {
        return selected;
      }
      if (
        fallbackOption &&
        fallbackOption.key === selectedKey &&
        selectedKey != null &&
        selectedKey !== ''
      ) {
        return fallbackOption;
      }
      return null;
    }, [selected, fallbackOption, selectedKey]);

    const filteredOptions = useMemo(() => {
      const q = searchQuery.trim().toLowerCase();
      if (!showSearch || !q) {
        return options;
      }
      return options.filter((o) =>
        String(o.label ?? '')
          .toLowerCase()
          .includes(q),
      );
    }, [options, searchQuery, showSearch]);

    const displayText = useMemo(() => {
      if (selected) {
        return selected.label;
      }
      if (
        selectedKey != null &&
        selectedKey !== '' &&
        fallbackLabel != null &&
        fallbackLabel !== ''
      ) {
        return fallbackLabel;
      }
      return null;
    }, [selected, selectedKey, fallbackLabel]);

    useEffect(() => {
      if (!open) {
        return undefined;
      }
      const onDocMouseDown = (e: MouseEvent) => {
        if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', onDocMouseDown);
      return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [open]);

    const handleToggle = useCallback(() => {
      if (!disabled && options.length > 0) {
        setOpen((v) => !v);
      }
    }, [disabled, options.length]);

    const handleSearchMouseDown = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
      },
      [],
    );

    const handleSelect = useCallback(
      (option: SimpleDropdownOption) => {
        onChange(option);
        setOpen(false);
      },
      [onChange],
    );

    const rootClass = classNames('bs-simple-options-dropdown', className, {
      'bs-simple-options-dropdown--open': open,
      'bs-simple-options-dropdown--disabled': disabled,
    });

    return (
      <div ref={rootRef} className={rootClass}>
        <button
          type="button"
          className="bs-simple-options-dropdown__trigger regular-2 gray-txt-9"
          onClick={handleToggle}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span
            className={classNames('bs-simple-options-dropdown__value', {
              'bs-simple-options-dropdown__value--placeholder': !displayText,
            })}
          >
            {renderOptionIcon && selectedForIcon ? (
              <span className="bs-simple-options-dropdown__value-inner">
                <span className="bs-simple-options-dropdown__value-icon">
                  {renderOptionIcon(selectedForIcon)}
                </span>
                <span className="bs-simple-options-dropdown__value-text">
                  {displayText ?? placeholder}
                </span>
              </span>
            ) : (
              displayText ?? placeholder
            )}
          </span>
          <span className="bs-simple-options-dropdown__chevron" aria-hidden>
            {open ? <ChevronUp /> : <ChevronDown />}
          </span>
        </button>
        {open && options.length > 0 && (
          <div
            className={classNames(
              'bs-simple-options-dropdown__menu',
              menuClassName,
            )}
          >
            {showSearch && (
              <div
                className="bs-simple-options-dropdown__search-wrap"
                role="presentation"
                onMouseDown={handleSearchMouseDown}
              >
                <Search width={16} height={16} className="simple-search-icon" />
                <input
                  ref={searchInputRef}
                  type="search"
                  className="bs-simple-options-dropdown__search-input regular-2 gray-txt-9"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label={searchPlaceholder}
                  autoComplete="off"
                />
              </div>
            )}
            {filteredOptions.length === 0 ? (
              <div className="bs-simple-options-dropdown__empty regular-2 gray-txt-11">
                No results found
              </div>
            ) : (
              <ul className="bs-simple-options-dropdown__list" role="listbox">
                {filteredOptions.map((opt) => (
                  <li key={String(opt.key)} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={opt.key === selectedKey}
                      className={classNames(
                        'bs-simple-options-dropdown__option regular-2 gray-txt-9',
                        {
                          'bs-simple-options-dropdown__option--selected':
                            opt.key === selectedKey,
                        },
                      )}
                      onClick={() => handleSelect(opt)}
                    >
                      {renderOptionIcon ? (
                        <span className="bs-simple-options-dropdown__option-inner">
                          <span className="bs-simple-options-dropdown__option-icon">
                            {renderOptionIcon(opt)}
                          </span>
                          <span className="bs-simple-options-dropdown__option-label">
                            {opt.label}
                          </span>
                        </span>
                      ) : (
                        opt.label
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  },
);

SimpleOptionsDropdown.displayName = 'SimpleOptionsDropdown';

export default SimpleOptionsDropdown;
